import { addDays, endOfMonth, format, isAfter, isBefore, startOfMonth } from 'date-fns';
import type { ReportDateSettings } from 'c-reports/Types';
import { ReportDateTimeframe } from 'c-reports/Types';
import { getClosestDayOfCurrentWeek } from 'c-lib';
import { ChartDataKeyDateFormat } from 'c-reports/Generators';

// The date window a report opens on, and the key its data is cached under.

/**
 * The first day of the window a report opens on, snapped to the timeframe: the configured first
 * day of the week for weekly views, the 1st for monthly ones, and otherwise the requested start
 * kept within the min/max dates.
 */
export const figureOutStartDate = (
    { minDate, maxDate, timeframe, firstDayOfWeek }: ReportDateSettings,
    initialStartDate?: Date,
): Date | undefined => {
    const theDate = initialStartDate ?? minDate;

    if (timeframe === ReportDateTimeframe.Weekly || timeframe === ReportDateTimeframe.BiWeekly) {
        // an unset first day is NaN arithmetic, an invalid date, as it always was
        return getClosestDayOfCurrentWeek(Number(firstDayOfWeek), theDate);
    }

    if (timeframe === ReportDateTimeframe.Monthly) {
        return startOfMonth(theDate ?? Number.NaN);
    }

    /**
     * initial start date is after the max date, return the max date
     */
    if (initialStartDate != null && maxDate != null && isAfter(initialStartDate, maxDate)) {
        return maxDate;
    }

    /**
     * if the initial start date is before the min date then correct it and use the min date instead
     */
    return initialStartDate != null && minDate != null && isBefore(initialStartDate, minDate)
        ? minDate
        : initialStartDate;
};

/** The last day of the window: a fixed length from the start, or the requested end capped at maxDate. */
export const figureOutEndDate = (
    { maxDate, timeframe }: ReportDateSettings,
    initialEndDate: Date,
    startDate: Date,
): Date | undefined => {
    if (timeframe === ReportDateTimeframe.Daily) {
        return startDate;
    }

    if (timeframe === ReportDateTimeframe.Weekly) {
        return addDays(startDate, 6);
    }

    if (timeframe === ReportDateTimeframe.BiWeekly) {
        return addDays(startDate, 13);
    }

    if (timeframe === ReportDateTimeframe.Monthly) {
        return endOfMonth(startDate);
    }

    return maxDate == null || !isAfter(initialEndDate, maxDate) ? initialEndDate : maxDate;
};

export const generateRequestKey = (
    prefix: string,
    startDate: Date,
    endDate: Date,
    timeframe: ReportDateTimeframe,
) =>
    `${prefix}-${format(startDate, ChartDataKeyDateFormat)}-${format(
        endDate,
        ChartDataKeyDateFormat,
    )}-${timeframe}`;
